import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, OpenInFull as OpenInFullIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { feeMonitorFrequencyToDisplayLabel, feeMonitorFrequencyToPeriodMonths } from "../utils/feeMonitorUtils";

const formatCurrency2 = (value) => {
  const number = typeof value === "number" ? value : Number(value ?? 0);
  return number.toLocaleString("nl-NL", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatCurrencyEUR = (value) => {
  const number = typeof value === "number" ? value : Number(value ?? 0);
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(number);
};

const formatNumber = (value, decimals = 2) => {
  const number = typeof value === "number" ? value : Number(value ?? 0);
  if (!Number.isFinite(number)) return "-";
  return Number(number).toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const parseYearMonth = (yearMonth) => {
  const str = String(yearMonth || "");
  const [yStr, mStr] = str.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return null;
  return { y, m };
};

const yearMonthKey = (y, m) => `${y}-${String(m).padStart(2, "0")}`;

const addMonths = (y, m, deltaMonths) => {
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(deltaMonths)) return null;
  const base = y * 12 + (m - 1);
  const next = base + deltaMonths;
  const nextY = Math.floor(next / 12);
  const nextM = (next % 12) + 1;
  if (!Number.isFinite(nextY) || !Number.isFinite(nextM) || nextM < 1 || nextM > 12) return null;
  return { y: nextY, m: nextM };
};

const quarterFromMonth = (m) => Math.floor((m - 1) / 3) + 1;


const scaleFromVolumeMonthly = (tierLines, volumeMonthly) => {
  if (!Array.isArray(tierLines) || tierLines.length === 0) return 0;
  const v = Number(volumeMonthly ?? 0);

  const sorted = [...tierLines].sort((a, b) => Number(a.scale ?? 0) - Number(b.scale ?? 0));
  for (const t of sorted) {
    const from = Number(t.from ?? 0);
    const to = Number(t.to ?? 0);
    if (from <= v && v <= to) return Number(t.scale ?? 0);
  }

  // Clamp outside.
  const minFrom = Math.min(...sorted.map((t) => Number(t.from ?? 0)));
  if (v < minFrom) return Number(sorted[0].scale ?? 0);
  return Number(sorted[sorted.length - 1].scale ?? 0);
};

const findTierLineByScale = (tierLines, scale) => {
  if (!Array.isArray(tierLines) || tierLines.length === 0) return null;
  return tierLines.find((t) => Number(t.scale) === Number(scale)) || null;
};

const labelForPeriodEnd = (endY, endM, periodMonths) => {
  if (!Number.isFinite(endY) || !Number.isFinite(endM)) return "";
  if (periodMonths === 1) return `${endY}-${String(endM).padStart(2, "0")}`;
  if (periodMonths === 3) return `${endY}-Q${quarterFromMonth(endM)}`;
  if (periodMonths === 6) return `${endY}-H${endM <= 6 ? 1 : 2}`;
  if (periodMonths === 12) return String(endY);
  return `${endY}-${String(endM).padStart(2, "0")}`;
};

const getPeriodMonths = (item) => {
  return feeMonitorFrequencyToPeriodMonths(item?.frequency);
};

const buildPeriodBars = (trendPoints, item, periodCount = 4, anchorOverride = null) => {
  if (!Array.isArray(trendPoints) || trendPoints.length === 0) return [];

  const periodMonths = getPeriodMonths(item);
  if (!Number.isFinite(periodMonths) || periodMonths <= 0) return [];

  const pointMap = new Map();
  const parsedPoints = trendPoints
    .map((p) => {
      const ym = parseYearMonth(p.month);
      if (!ym) return null;
      return { y: ym.y, m: ym.m, v: Number(p.vatVolume ?? 0) };
    })
    .filter(Boolean);

  if (parsedPoints.length === 0) return [];

  parsedPoints.forEach((p) => {
    pointMap.set(yearMonthKey(p.y, p.m), Number.isFinite(p.v) ? p.v : 0);
  });

  // Anchor periods on the backend-provided "periodEnd" when available.
  // This matters for halfYear/year where the period's total is now placed on the last month of the period.
  const anchorFromLatest = parseYearMonth(item?.latest?.periodEnd);
  const lastTrendPoint = parsedPoints[parsedPoints.length - 1];
  const anchor = anchorOverride || anchorFromLatest || lastTrendPoint;
  if (!anchor) return [];

  const barsNewestFirst = [];
  for (let i = 0; i < periodCount; i++) {
    const end = addMonths(anchor.y, anchor.m, -i * periodMonths);
    if (!end) break;
    const start = addMonths(end.y, end.m, -(periodMonths - 1));
    if (!start) break;

    let sum = 0;
    for (let j = 0; j < periodMonths; j++) {
      const cur = addMonths(start.y, start.m, j);
      if (!cur) continue;
      sum += Number(pointMap.get(yearMonthKey(cur.y, cur.m)) ?? 0);
    }

    // Backend can provide period totals by placing the value on the last month of the period (e.g. halfYear/year).
    // To support all cases consistently, we display PERIOD TOTALS in the chart:
    // - if monthly values are repeated: total = avg * months
    // - if only the last month has a value: total = that value
    // - if genuine monthly values exist: total = sum of months
    const periodTotal = sum;
    const avgMonthly = periodMonths > 0 ? periodTotal / periodMonths : 0;
    barsNewestFirst.push({
      label: labelForPeriodEnd(end.y, end.m, periodMonths),
      value: Number.isFinite(periodTotal) ? periodTotal : 0,
      avgMonthly: Number.isFinite(avgMonthly) ? avgMonthly : 0,
      periodMonths,
      endY: end.y,
      endM: end.m,
    });
  }

  return barsNewestFirst.reverse(); // oldest -> newest for display
};

const buildQuarterBarsForYearIfNeeded = (trendPointsForYear, year) => {
  if (!Number.isFinite(year) || !Array.isArray(trendPointsForYear) || trendPointsForYear.length === 0) return null;
  const parsedPoints = trendPointsForYear
    .map((p) => {
      const ym = parseYearMonth(p?.month);
      if (!ym) return null;
      return { y: ym.y, m: ym.m, v: Number(p?.vatVolume ?? 0) };
    })
    .filter(Boolean)
    .filter((p) => p.y === year);

  if (parsedPoints.length === 0) return null;

  const nonZero = parsedPoints.filter((p) => Number(p.v ?? 0) !== 0);
  const onlyLastMonthHasValue = nonZero.length === 1 && Number(nonZero[0]?.m) === 12;
  if (!onlyLastMonthHasValue) return null;

  const yearTotal = parsedPoints.reduce((sum, p) => sum + Number(p.v ?? 0), 0);
  if (!Number.isFinite(yearTotal) || yearTotal === 0) return null;

  const quarterTotal = yearTotal / 4;
  const avgMonthly = yearTotal / 12;
  const bars = [1, 2, 3, 4].map((q) => {
    const endM = q * 3;
    return {
      label: `${year}-Q${q}`,
      value: Number.isFinite(quarterTotal) ? quarterTotal : 0,
      avgMonthly: Number.isFinite(avgMonthly) ? avgMonthly : 0,
      periodMonths: 3,
      endY: year,
      endM,
    };
  });

  return { bars, yearTotal };
};

const buildQuarterBarsForSelectedYear = (trendPointsForYear, year) => {
  if (!Number.isFinite(year) || !Array.isArray(trendPointsForYear) || trendPointsForYear.length === 0) return null;

  const pointMap = new Map();
  const parsedPoints = trendPointsForYear
    .map((p) => {
      const ym = parseYearMonth(p?.month);
      if (!ym) return null;
      return { y: ym.y, m: ym.m, v: Number(p?.vatVolume ?? 0) };
    })
    .filter(Boolean)
    .filter((p) => p.y === year);

  if (parsedPoints.length === 0) return null;

  parsedPoints.forEach((p) => {
    pointMap.set(yearMonthKey(p.y, p.m), Number.isFinite(p.v) ? p.v : 0);
  });

  return [1, 2, 3, 4].map((q) => {
    const startM = (q - 1) * 3 + 1;
    const endM = q * 3;
    let quarterTotal = 0;
    let hasData = false;

    for (let month = startM; month <= endM; month++) {
      const value = pointMap.get(yearMonthKey(year, month));
      if (value !== undefined) {
        quarterTotal += Number(value ?? 0);
        hasData = true;
      }
    }

    return {
      label: `${year}-Q${q}`,
      value: Number.isFinite(quarterTotal) ? quarterTotal : 0,
      avgMonthly: Number.isFinite(quarterTotal) ? quarterTotal / 3 : 0,
      periodMonths: 3,
      endY: year,
      endM,
      hasData,
    };
  });
};

const buildHalfYearBarsForSelectedYear = (trendPointsForYear, year) => {
  if (!Number.isFinite(year) || !Array.isArray(trendPointsForYear) || trendPointsForYear.length === 0) return null;

  const pointMap = new Map();
  const parsedPoints = trendPointsForYear
    .map((p) => {
      const ym = parseYearMonth(p?.month);
      if (!ym) return null;
      return { y: ym.y, m: ym.m, v: Number(p?.vatVolume ?? 0) };
    })
    .filter(Boolean)
    .filter((p) => p.y === year);

  if (parsedPoints.length === 0) return null;

  parsedPoints.forEach((p) => {
    pointMap.set(yearMonthKey(p.y, p.m), Number.isFinite(p.v) ? p.v : 0);
  });

  return [1, 2].map((half) => {
    const startM = half === 1 ? 1 : 7;
    const endM = half === 1 ? 6 : 12;
    let halfYearTotal = 0;
    let hasData = false;

    for (let month = startM; month <= endM; month++) {
      const value = pointMap.get(yearMonthKey(year, month));
      if (value !== undefined) {
        halfYearTotal += Number(value ?? 0);
        hasData = true;
      }
    }

    return {
      label: `${year}-H${half}`,
      value: Number.isFinite(halfYearTotal) ? halfYearTotal : 0,
      avgMonthly: Number.isFinite(halfYearTotal) ? halfYearTotal / 6 : 0,
      periodMonths: 6,
      endY: year,
      endM,
      hasData,
    };
  });
};

const FeeMonitorTrendPanel = ({ item, onClose, fallbackTierLines = [] }) => {
  const theme = useTheme();
  const [hoveredBar, setHoveredBar] = useState(null);
  const [hoveredBarLarge, setHoveredBarLarge] = useState(null);
  const [isLargeOpen, setIsLargeOpen] = useState(false);
  const chartWrapRef = useRef(null);
  const chartWrapLargeRef = useRef(null);
  const [chartScale, setChartScale] = useState(1);
  const [chartScaleLarge, setChartScaleLarge] = useState(1);
  const tierLines = useMemo(() => {
    if (Array.isArray(item?.tierLines) && item.tierLines.length > 0) return item.tierLines;
    if (Array.isArray(fallbackTierLines)) return fallbackTierLines;
    return [];
  }, [item?.tierLines, fallbackTierLines]);
  const trendPoints = useMemo(() => {
    return Array.isArray(item?.trendPoints) ? item.trendPoints : [];
  }, [item?.trendPoints]);
  const availableYears = useMemo(() => {
    if (!Array.isArray(trendPoints)) return [];
    const years = trendPoints
      .map((p) => parseYearMonth(p?.month)?.y)
      .filter((y) => Number.isFinite(y));
    return [...new Set(years)].sort((a, b) => a - b);
  }, [trendPoints]);
  const latestYear = availableYears.length ? availableYears[availableYears.length - 1] : null;
  const [selectedYear, setSelectedYear] = useState(latestYear);
  const trendPointsForYear = useMemo(() => {
    if (!Number.isFinite(selectedYear)) return trendPoints;
    return trendPoints.filter((p) => parseYearMonth(p?.month)?.y === selectedYear);
  }, [trendPoints, selectedYear]);
  const trendAnchorForYear = useMemo(() => {
    if (!Number.isFinite(selectedYear) || trendPointsForYear.length === 0) return null;
    const lastPoint = trendPointsForYear[trendPointsForYear.length - 1];
    return parseYearMonth(lastPoint?.month);
  }, [trendPointsForYear, selectedYear]);
  const yearCatchupsAll = useMemo(() => {
    if (!Array.isArray(item?.yearCatchups)) return [];
    return item.yearCatchups.filter((c) => Number(c?.vatVolume ?? 0) > 0);
  }, [item?.yearCatchups]);
  const yearCatchups = useMemo(() => {
    if (!Number.isFinite(selectedYear)) return yearCatchupsAll;
    return yearCatchupsAll.filter((c) => Number(c?.year) === Number(selectedYear));
  }, [yearCatchupsAll, selectedYear]);
  const quarterlyDistribution = useMemo(() => {
    return buildQuarterBarsForYearIfNeeded(trendPointsForYear, selectedYear);
  }, [trendPointsForYear, selectedYear]);
  const latestCatchup = useMemo(() => {
    if (!yearCatchupsAll.length) return null;
    return [...yearCatchupsAll].sort((a, b) => Number(b?.year ?? 0) - Number(a?.year ?? 0))[0];
  }, [yearCatchupsAll]);
  const hasPricingAgreement = item?.hasPricingAgreement !== false;
  const productLabel = item?.productType || "VAT";
  const periodMonths = getPeriodMonths(item);
  const isQuarterlyFrequency = periodMonths === 3;
  const isHalfYearFrequency = periodMonths === 6;
  const quarterBarsForSelectedYear = useMemo(() => {
    if (!isQuarterlyFrequency) return null;
    return buildQuarterBarsForSelectedYear(trendPointsForYear, selectedYear);
  }, [isQuarterlyFrequency, trendPointsForYear, selectedYear]);
  const halfYearBarsForSelectedYear = useMemo(() => {
    if (!isHalfYearFrequency) return null;
    return buildHalfYearBarsForSelectedYear(trendPointsForYear, selectedYear);
  }, [isHalfYearFrequency, trendPointsForYear, selectedYear]);
  const displayBars = useMemo(() => {
    if (quarterlyDistribution?.bars?.length) return quarterlyDistribution.bars;
    if (quarterBarsForSelectedYear?.length) return quarterBarsForSelectedYear;
    if (halfYearBarsForSelectedYear?.length) return halfYearBarsForSelectedYear;
    return buildPeriodBars(trendPointsForYear, item, 4, trendAnchorForYear);
  }, [quarterlyDistribution, quarterBarsForSelectedYear, halfYearBarsForSelectedYear, trendPointsForYear, item, trendAnchorForYear]);
  const isSelectedYearQuarterView = isQuarterlyFrequency && !!quarterBarsForSelectedYear?.length;
  const isSelectedYearHalfYearView = isHalfYearFrequency && !!halfYearBarsForSelectedYear?.length;
  const chartSubtitle = isSelectedYearQuarterView
    ? `Selected year quarters (${productLabel} volume per quarter)`
    : isSelectedYearHalfYearView
    ? `Selected year half-years (${productLabel} volume per half-year)`
    : `Last 4 periods (${productLabel} volume per period)`;
  const baselineScale = item?.baseline?.scale;
  const latestScale = item?.latest?.scale;
  const discountPct = Number(item?.baseline?.discountPct ?? 0);
  const vfPct = Number(item?.baseline?.vfPct ?? 0);
  // Tooltip styling: keep readable contrast in both light/dark mode.
  const diffTooltipBg = "rgba(20, 20, 20, 0.96)";
  const diffTooltipText = "rgba(255, 255, 255, 0.92)";
  const diffTooltipMutedText = "rgba(255, 255, 255, 0.72)";
  const diffTooltipDivider = "rgba(255, 255, 255, 0.18)";

  const baselineTierLine = useMemo(() => findTierLineByScale(tierLines, baselineScale), [tierLines, baselineScale]);
  const latestTierLine = useMemo(() => findTierLineByScale(tierLines, latestScale), [tierLines, latestScale]);
  const baselineTierFeeGross = Number(baselineTierLine?.monthFee ?? 0);
  const latestTierFeeGross = Number(latestTierLine?.monthFee ?? 0);
  // NOTE: VF is intentionally NOT applied in this UI explanation.
  // The backend remains the source of truth for deltaFeeMonthly; this is just an explanation tooltip.
  const baselineTierFeeNet = baselineTierFeeGross * (1 - discountPct / 100);
  const latestTierFeeNet = latestTierFeeGross * (1 - discountPct / 100);

  const diffTooltip = useMemo(() => {
    if (!item?.latest || !hasPricingAgreement) return null;
    const deltaNetBase = Number(item.latest.deltaFeeMonthly ?? 0);
    const catchupDeltaMonthly = latestCatchup ? Number(latestCatchup?.deltaFeeMonthly ?? 0) : 0;
    const deltaNet = latestCatchup ? catchupDeltaMonthly : deltaNetBase;
    const netMultiplier = 1 - discountPct / 100;
    const netMultiplierPct = Math.round(netMultiplier * 1000) / 10; // 1 decimal

    return (
      <Box sx={{ maxWidth: 360 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: diffTooltipText }}>
          Net fee difference (per month)
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: diffTooltipText }}>
          Baseline (gross): {formatCurrency2(baselineTierFeeGross)}/m
        </Typography>
        <Typography variant="body2" sx={{ color: diffTooltipText }}>
          Current (gross): {formatCurrency2(latestTierFeeGross)}/m
        </Typography>
        {discountPct > 0 ? (
          <Typography variant="body2" sx={{ mt: 0.5, color: diffTooltipText }}>
            Discount {discountPct}% → net = gross × {netMultiplierPct}%
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ mt: 0.5, color: diffTooltipText }}>
            No discount → net = gross
          </Typography>
        )}
        {vfPct > 0 ? (
          <Typography variant="body2" sx={{ color: diffTooltipMutedText }}>
            Note: VF ({vfPct}%) is not applied in this explanation.
          </Typography>
        ) : null}
        <Divider sx={{ my: 1, borderColor: diffTooltipDivider }} />
        <Typography variant="body2" sx={{ color: diffTooltipText }}>
          Baseline (net): {formatCurrency2(baselineTierFeeNet)}/m
        </Typography>
        <Typography variant="body2" sx={{ color: diffTooltipText }}>
          Current (net): {formatCurrency2(latestTierFeeNet)}/m
        </Typography>
        {latestCatchup ? (
          <Typography variant="body2" sx={{ mt: 0.5, color: diffTooltipText }}>
            Catch-up net difference: {formatCurrency2(catchupDeltaMonthly)}/m
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700, color: diffTooltipText }}>
          Difference = {formatCurrency2(deltaNet)}/m
        </Typography>
      </Box>
    );
  }, [
    item?.latest,
    baselineTierFeeGross,
    latestTierFeeGross,
    discountPct,
    vfPct,
    diffTooltipText,
    diffTooltipMutedText,
    diffTooltipDivider,
    baselineTierFeeNet,
    latestTierFeeNet,
    hasPricingAgreement,
    latestCatchup,
  ]);
  const netDifferenceMonthly = useMemo(() => {
    const deltaMonthly = latestCatchup ? Number(latestCatchup?.deltaFeeMonthly ?? 0) : Number(item?.latest?.deltaFeeMonthly ?? 0);
    return Number.isFinite(deltaMonthly) ? deltaMonthly : 0;
  }, [item?.latest?.deltaFeeMonthly, latestCatchup]);

  useEffect(() => {
    if (!availableYears.length) return;
    if (!Number.isFinite(selectedYear) || !availableYears.includes(selectedYear)) {
      setSelectedYear(latestYear);
    }
  }, [availableYears, latestYear, selectedYear]);

  const latestBarForScale = useMemo(() => {
    if (!displayBars.length) return null;
    return [...displayBars].reverse().find((bar) => bar?.hasData !== false) || displayBars[displayBars.length - 1];
  }, [displayBars]);
  const latestPeriodMonthly = useMemo(() => {
    return Number(latestBarForScale?.avgMonthly ?? 0);
  }, [latestBarForScale]);
  const currentScale = useMemo(() => scaleFromVolumeMonthly(tierLines, latestPeriodMonthly), [tierLines, latestPeriodMonthly]);
  const currentTierLine = useMemo(() => findTierLineByScale(tierLines, currentScale), [tierLines, currentScale]);
  const displayedCurrentScale = hasPricingAgreement ? latestScale : currentScale || 0;

  const chart = useMemo(() => {
    const w = 620;
    const h = 340;
    // More left/right padding so thousand-separated y-axis labels (e.g. 1.500) don't get clipped.
    const padding = 44;

    const defaultPeriodMonths = getPeriodMonths(item);
    const bars = displayBars;
    const periodMonths = bars.length ? Number(bars[0].periodMonths ?? defaultPeriodMonths) : defaultPeriodMonths;
    const periodMultiplier = Number.isFinite(periodMonths) && periodMonths > 0 ? periodMonths : 1;
    const barValues = bars.map((b) => Number(b.value ?? 0));
    const maxData = Math.max(...barValues, 0);

    // IMPORTANT: Don't scale to the absolute max staffel (often 9,999,999). That would flatten all real data.
    // Instead, scale to the highest "relevant" tier: baseline tier and latest tier (plus the data itself).
    const baselineLine = tierLines.find((t) => t.scale === baselineScale);
    const chartLatestScale = Number(currentScale);
    const latestLine = tierLines.find((t) => t.scale === chartLatestScale);
    const nextTierLine = tierLines.find((t) => Number(t.scale) === Number(currentScale + 1));
    const maxScale = tierLines.length ? Math.max(...tierLines.map((t) => Number(t.scale ?? 0))) : 0;
    const currentLine = currentTierLine;
    const isLastTier =
      Number.isFinite(currentScale) &&
      Number.isFinite(maxScale) &&
      maxScale > 0 &&
      Number(currentScale) === Number(maxScale);
    const currentTo = Number(currentLine?.to ?? 0);
    const currentFrom = Number(currentLine?.from ?? 0);
    const hugeUpperBound = Number.isFinite(currentTo) && currentTo >= 1_000_000;

    const maxRelevantTierMonthlyBase = Math.max(Number(baselineLine?.to ?? 0), Number(latestLine?.to ?? 0));
    const includeExtraTier = Number(baselineScale) >= Number(chartLatestScale);
    const maxRelevantTierMonthly = includeExtraTier
      ? Math.max(maxRelevantTierMonthlyBase, Number(nextTierLine?.to ?? 0))
      : maxRelevantTierMonthlyBase;
    const maxRelevantTier = maxRelevantTierMonthly * periodMultiplier;

    // If we're in the last tier with a huge upper bound (e.g. 1,500 -> 9,999,999),
    // cap the chart max to keep the bars readable. Target: tallest bar ~90% of chart height.
    const autoCapMaxV = maxData > 0 ? maxData / 0.9 : 1;
    const maxV =
      isLastTier && hugeUpperBound
        ? Math.max(autoCapMaxV, currentFrom * periodMultiplier * 1.05, 1)
        : Math.max(maxData, maxRelevantTier, 1);

    const topLabel = isLastTier && hugeUpperBound ? "∞" : Math.round(maxV).toLocaleString("nl-NL");
    const innerH = Math.max(1, h - padding * 2);
    const innerW = Math.max(1, w - padding * 2);

    // Smart axis scaling:
    // When volumes grow high, linear scaling makes the lower tiers unreadable (too cramped).
    // Use a sqrt scale to expand the lower range while keeping labels as real values.
    const baselineTo = Number(baselineLine?.to ?? 0) * periodMultiplier;
    const shouldUseSqrtScale = maxV > 0 && baselineTo > 0 && maxV / baselineTo >= 2.2;
    const axisScaleMode = shouldUseSqrtScale ? "sqrt" : "linear";

    const toY = (v) => {
      const tLinear = clamp(v / maxV, 0, 1);
      const t = axisScaleMode === "sqrt" ? Math.sqrt(tLinear) : tLinear;
      return padding + (1 - t) * innerH;
    };

    const baselineBand = baselineLine
      ? {
          yTop: toY(Number(baselineLine.to ?? 0) * periodMultiplier),
          yBottom: toY(Number(baselineLine.from ?? 0) * periodMultiplier),
        }
      : null;

    const includeExtraTierBand = Number(baselineScale) >= Number(chartLatestScale);
    const nextTierBand = includeExtraTierBand && nextTierLine
      ? {
          yTop: toY(Number(nextTierLine.to ?? 0) * periodMultiplier),
          yBottom: toY(Number(nextTierLine.from ?? 0) * periodMultiplier),
        }
      : null;

    const yLines = tierLines
      .map((t) => Number(t.to ?? 0) * periodMultiplier)
      .filter((v) => Number.isFinite(v) && v > 0 && v < maxV)
      .sort((a, b) => a - b);

    // Build y-axis labels for dashed staffel boundaries, skipping labels that would overlap.
    const yLineLabels = [];
    const minPixelGap = 12;
    let lastY = null;
    [...yLines]
      .sort((a, b) => b - a)
      .forEach((v) => {
        const y = toY(v);
        if (lastY !== null && Math.abs(y - lastY) < minPixelGap) return;
        yLineLabels.push({ v, y });
        lastY = y;
      });

    // Distribute bars across the full plot width: 1 "slot" per period.
    // The bar stays relatively slim but each period uses the full width (better spacing/legibility).
    const barCount = Math.max(1, bars.length);
    const slotWidth = innerW / barCount;
    const barWidth = clamp(slotWidth * 0.55, 10, 60);

    const barRects = bars.map((b, idx) => {
      const slotX = padding + idx * slotWidth;
      const x = slotX + (slotWidth - barWidth) / 2;
      const y = toY(b.value);
      const y0 = toY(0);
      const height = Math.max(0, y0 - y);
      const barScale = scaleFromVolumeMonthly(tierLines, b.avgMonthly);
      const isInBaseline = Number.isFinite(baselineScale) && barScale === Number(baselineScale);
      return { ...b, x, y, width: barWidth, height, barScale, isInBaseline };
    });

    return {
      w,
      h,
      padding,
      toY,
      baselineBand,
      nextTierBand,
      yLines,
      yLineLabels,
      maxV,
      topLabel,
      axisScaleMode,
      bars: barRects,
    };
  }, [
    tierLines,
    baselineScale,
    item,
    currentScale,
    currentTierLine,
    displayBars,
  ]);

  const chartLarge = useMemo(() => {
    const w = 980;
    const h = 520;
    const padding = 56;

    const defaultPeriodMonths = getPeriodMonths(item);
    const bars = displayBars;
    const periodMonths = bars.length ? Number(bars[0].periodMonths ?? defaultPeriodMonths) : defaultPeriodMonths;
    const periodMultiplier = Number.isFinite(periodMonths) && periodMonths > 0 ? periodMonths : 1;
    const barValues = bars.map((b) => Number(b.value ?? 0));
    const maxData = Math.max(...barValues, 0);

    const baselineLine = tierLines.find((t) => t.scale === baselineScale);
    const chartLatestScale = Number(currentScale);
    const latestLine = tierLines.find((t) => t.scale === chartLatestScale);
    const nextTierLine = tierLines.find((t) => Number(t.scale) === Number(currentScale + 1));
    const maxScale = tierLines.length ? Math.max(...tierLines.map((t) => Number(t.scale ?? 0))) : 0;
    const currentLine = currentTierLine;
    const isLastTier =
      Number.isFinite(currentScale) &&
      Number.isFinite(maxScale) &&
      maxScale > 0 &&
      Number(currentScale) === Number(maxScale);
    const currentTo = Number(currentLine?.to ?? 0);
    const currentFrom = Number(currentLine?.from ?? 0);
    const hugeUpperBound = Number.isFinite(currentTo) && currentTo >= 1_000_000;

    const maxRelevantTierMonthlyBase = Math.max(Number(baselineLine?.to ?? 0), Number(latestLine?.to ?? 0));
    const includeExtraTier = Number(baselineScale) >= Number(chartLatestScale);
    const maxRelevantTierMonthly = includeExtraTier
      ? Math.max(maxRelevantTierMonthlyBase, Number(nextTierLine?.to ?? 0))
      : maxRelevantTierMonthlyBase;
    const maxRelevantTier = maxRelevantTierMonthly * periodMultiplier;

    const autoCapMaxV = maxData > 0 ? maxData / 0.9 : 1;
    const maxV =
      isLastTier && hugeUpperBound
        ? Math.max(autoCapMaxV, currentFrom * periodMultiplier * 1.05, 1)
        : Math.max(maxData, maxRelevantTier, 1);

    const topLabel = isLastTier && hugeUpperBound ? "∞" : Math.round(maxV).toLocaleString("nl-NL");
    const innerH = Math.max(1, h - padding * 2);
    const innerW = Math.max(1, w - padding * 2);

    const baselineTo = Number(baselineLine?.to ?? 0) * periodMultiplier;
    const shouldUseSqrtScale = maxV > 0 && baselineTo > 0 && maxV / baselineTo >= 2.2;
    const axisScaleMode = shouldUseSqrtScale ? "sqrt" : "linear";

    const toY = (v) => {
      const tLinear = clamp(v / maxV, 0, 1);
      const t = axisScaleMode === "sqrt" ? Math.sqrt(tLinear) : tLinear;
      return padding + (1 - t) * innerH;
    };

    const baselineBand = baselineLine
      ? {
          yTop: toY(Number(baselineLine.to ?? 0) * periodMultiplier),
          yBottom: toY(Number(baselineLine.from ?? 0) * periodMultiplier),
        }
      : null;

    const includeExtraTierBand = Number(baselineScale) >= Number(chartLatestScale);
    const nextTierBand = includeExtraTierBand && nextTierLine
      ? {
          yTop: toY(Number(nextTierLine.to ?? 0) * periodMultiplier),
          yBottom: toY(Number(nextTierLine.from ?? 0) * periodMultiplier),
        }
      : null;

    const yLines = tierLines
      .map((t) => Number(t.to ?? 0) * periodMultiplier)
      .filter((v) => Number.isFinite(v) && v > 0 && v < maxV)
      .sort((a, b) => a - b);

    const yLineLabels = [];
    const minPixelGap = 14;
    let lastY = null;
    [...yLines]
      .sort((a, b) => b - a)
      .forEach((v) => {
        const y = toY(v);
        if (lastY !== null && Math.abs(y - lastY) < minPixelGap) return;
        yLineLabels.push({ v, y });
        lastY = y;
      });

    const barCount = Math.max(1, bars.length);
    const slotWidth = innerW / barCount;
    const barWidth = clamp(slotWidth * 0.55, 10, 72);

    const barRects = bars.map((b, idx) => {
      const slotX = padding + idx * slotWidth;
      const x = slotX + (slotWidth - barWidth) / 2;
      const y = toY(b.value);
      const y0 = toY(0);
      const height = Math.max(0, y0 - y);
      const barScale = scaleFromVolumeMonthly(tierLines, b.avgMonthly);
      const isInBaseline = Number.isFinite(baselineScale) && barScale === Number(baselineScale);
      return { ...b, x, y, width: barWidth, height, barScale, isInBaseline };
    });

    return {
      w,
      h,
      padding,
      toY,
      baselineBand,
      nextTierBand,
      yLines,
      yLineLabels,
      maxV,
      topLabel,
      axisScaleMode,
      bars: barRects,
    };
  }, [
    tierLines,
    baselineScale,
    item,
    currentScale,
    currentTierLine,
    displayBars,
  ]);

  const hoveredTierLine = useMemo(() => {
    if (!hoveredBar?.barScale) return null;
    return (tierLines || []).find((t) => Number(t.scale) === Number(hoveredBar.barScale)) || null;
  }, [hoveredBar, tierLines]);

  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el) return;

    const updateScale = () => {
      // Scale factor between rendered SVG pixels and viewBox units.
      // We render SVG at 100% width; viewBox stays at chart.w.
      const w = el.clientWidth || 1;
      const next = w / chart.w;
      setChartScale(next > 0 ? next : 1);
    };

    updateScale();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateScale);
      ro.observe(el);
    } else {
      window.addEventListener("resize", updateScale);
    }

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, [chart.w]);

  useEffect(() => {
    const el = chartWrapLargeRef.current;
    if (!el) return;

    const updateScale = () => {
      const w = el.clientWidth || 1;
      const next = w / chartLarge.w;
      setChartScaleLarge(next > 0 ? next : 1);
    };

    updateScale();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateScale);
      ro.observe(el);
    } else {
      window.addEventListener("resize", updateScale);
    }

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, [chartLarge.w]);

  if (!item) return null;

  const axisTextColor = theme.palette.mode === "dark" ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.60)";
  const axisTextMutedColor = theme.palette.mode === "dark" ? "rgba(255,255,255,0.60)" : "rgba(0,0,0,0.45)";
  const axisLineColor = theme.palette.mode === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)";
  const baselineLineColor = theme.palette.mode === "dark" ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.38)";
  const borderLineColor = theme.palette.mode === "dark" ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.22)";

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        bgcolor: "background.paper",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            {item.countryCode} {productLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasPricingAgreement
              ? `Baseline scale ${baselineScale} • Current scale ${displayedCurrentScale}`
              : `No pricing agreement • Current scale ${displayedCurrentScale}`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Open larger chart">
            <IconButton onClick={() => setIsLargeOpen(true)} size="small" aria-label="Open chart larger">
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {!hasPricingAgreement ? (
          <Chip label="No pricing agreement" color="error" size="small" variant="outlined" />
        ) : item.latest ? (
          <Tooltip
            title={diffTooltip || ""}
            arrow
            placement="bottom-start"
            componentsProps={{
              tooltip: { sx: { bgcolor: diffTooltipBg, color: diffTooltipText, maxWidth: 420 } },
              arrow: { sx: { color: diffTooltipBg } },
            }}
          >
            <Chip
              label={`Net difference: ${formatCurrency2(netDifferenceMonthly)}/m`}
              color={
                netDifferenceMonthly < 0
                  ? "error"
                  : netDifferenceMonthly > 0
                  ? "warning"
                  : "success"
              }
              size="small"
              variant="outlined"
            />
          </Tooltip>
        ) : (
          <Chip label="No claims found" size="small" variant="outlined" />
        )}
        {item.frequency ? (
          <Chip label={`Frequency: ${feeMonitorFrequencyToDisplayLabel(item.frequency.label)}`} size="small" variant="outlined" />
        ) : null}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {hasPricingAgreement
          ? "Net difference is based on the latest period (not the selected year)."
          : "Claims were found for this country, but no pricing agreement exists yet."}
      </Typography>

      <Divider />

      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1 }}>
          <Typography variant="subtitle2">{chartSubtitle}</Typography>
          {availableYears.length > 0 ? (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {availableYears.map((year) => (
                <Chip
                  key={year}
                  label={String(year)}
                  size="small"
                  variant={year === selectedYear ? "filled" : "outlined"}
                  color={year === selectedYear ? "primary" : "default"}
                  onClick={() => setSelectedYear(year)}
                  aria-label={`Select year ${year}`}
                />
              ))}
            </Box>
          ) : null}
        </Box>
        <Box ref={chartWrapRef} sx={{ position: "relative", overflow: "visible" }}>
          <Box sx={{ overflow: "hidden" }}>
            <svg
              width="100%"
              height={chart.h}
              viewBox={`0 0 ${chart.w} ${chart.h}`}
              preserveAspectRatio="xMidYMid meet"
              shapeRendering="crispEdges"
              style={{ display: "block" }}
            >
              <rect x="0" y="0" width={chart.w} height={chart.h} fill="transparent" />

            {/* Y-axis labels (lightweight) */}
            <text
              x={chart.padding - 8}
              y={chart.padding + 4}
              textAnchor="end"
              fontSize="11"
              fill={axisTextColor}
            >
              {chart.topLabel}
            </text>
            <text
              x={chart.padding - 8}
              y={chart.h - chart.padding + 4}
              textAnchor="end"
              fontSize="11"
              fill={axisTextColor}
            >
              0
            </text>

            {/* Staffel boundary labels (match dashed lines) */}
            {chart.yLineLabels.map(({ v, y }) => (
              <text
                key={`yline-${v}`}
                x={chart.padding - 8}
                y={y + 4}
                textAnchor="end"
                fontSize="11"
                fill={axisTextMutedColor}
              >
                {Number(v).toLocaleString("nl-NL")}
              </text>
            ))}

            {chart.baselineBand ? (
              <rect
                x={chart.padding}
                y={Math.min(chart.baselineBand.yTop, chart.baselineBand.yBottom)}
                width={chart.w - chart.padding * 2}
                height={Math.abs(chart.baselineBand.yBottom - chart.baselineBand.yTop)}
                fill="rgba(46, 107, 255, 0.12)"
              />
            ) : null}

            {/* Baseline tier boundaries: solid lines (instead of green lines) */}
            {chart.baselineBand ? (
              <g>
                <line
                  x1={chart.padding}
                  y1={chart.baselineBand.yTop}
                  x2={chart.w - chart.padding}
                  y2={chart.baselineBand.yTop}
                  stroke={baselineLineColor}
                  strokeWidth="2"
                />
                <line
                  x1={chart.padding}
                  y1={chart.baselineBand.yBottom}
                  x2={chart.w - chart.padding}
                  y2={chart.baselineBand.yBottom}
                  stroke={baselineLineColor}
                  strokeWidth="2"
                />
              </g>
            ) : null}

            {/* Next tier overlay (tier ABOVE current): neutral (no color) */}
            {chart.nextTierBand ? (
              <g>
                <line
                  x1={chart.padding}
                  y1={chart.nextTierBand.yTop}
                  x2={chart.w - chart.padding}
                  y2={chart.nextTierBand.yTop}
                  stroke={axisLineColor}
                  strokeWidth="2"
                  strokeDasharray="6 6"
                />
                <line
                  x1={chart.padding}
                  y1={chart.nextTierBand.yBottom}
                  x2={chart.w - chart.padding}
                  y2={chart.nextTierBand.yBottom}
                  stroke={axisLineColor}
                  strokeWidth="2"
                  strokeDasharray="6 6"
                />
              </g>
            ) : null}

            {chart.yLines.map((v) => {
              const y = chart.toY(v);
              return (
                <line
                  key={v}
                  x1={chart.padding}
                  y1={y}
                  x2={chart.w - chart.padding}
                  y2={y}
                  stroke={axisLineColor}
                  strokeDasharray="6 6"
                />
              );
            })}

            <rect
              x={chart.padding}
              y={chart.padding}
              width={chart.w - chart.padding * 2}
              height={chart.h - chart.padding * 2}
              fill="none"
              stroke={borderLineColor}
            />

              {chart.bars.map((b, idx) => (
                <rect
                  key={`${b.label}-${idx}`}
                  x={b.x}
                  y={b.y}
                  width={b.width}
                  height={b.height}
                  rx="0"
                  fill={
                    b.isInBaseline
                      ? idx === chart.bars.length - 1
                        ? "rgba(46, 224, 122, 0.90)"
                        : "rgba(46, 224, 122, 0.65)"
                      : idx === chart.bars.length - 1
                      ? "rgba(46, 107, 255, 0.85)"
                      : "rgba(46, 107, 255, 0.55)"
                  }
                  onMouseEnter={() => setHoveredBar(b)}
                  onMouseLeave={() => setHoveredBar(null)}
                />
              ))}

              {chart.bars.map((b, idx) => (
                <text
                  key={`lbl-${b.label}-${idx}`}
                  x={b.x + b.width / 2}
                  y={chart.h - 10}
                  textAnchor="middle"
                  fontSize="11"
                fill={axisTextColor}
                >
                  {b.label}
                </text>
              ))}
            </svg>
          </Box>

          {hoveredBar ? (
            (() => {
              const tooltipWidth = 240; // keep in sync with minWidth below
              const half = tooltipWidth / 2;
              const minX = chart.padding * chartScale + half + 6;
              const maxX = (chart.w - chart.padding) * chartScale - half - 6;
              const centerX = (hoveredBar.x + hoveredBar.width / 2) * chartScale;
              const left = clamp(centerX, minX, maxX);
              return (
            <Box
              sx={{
                position: "absolute",
                left,
                top: Math.max(8, hoveredBar.y * chartScale - 8),
                transform: "translate(-50%, -100%)",
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
                px: 1.25,
                py: 0.75,
                boxShadow: 3,
                pointerEvents: "none",
                minWidth: tooltipWidth,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {hoveredBar.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {Number(hoveredBar.value ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 2 })} (VAT volume)
              </Typography>
              {Number(hoveredBar.periodMonths ?? 1) > 1 ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Avg/month: {Number(hoveredBar.avgMonthly ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 2 })}
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Scale: {hoveredBar.barScale}
                {hoveredTierLine
                  ? ` • Range: ${Number(hoveredTierLine.from * Number(hoveredBar.periodMonths ?? 1)).toLocaleString("nl-NL")} – ${Number(
                      hoveredTierLine.to * Number(hoveredBar.periodMonths ?? 1)
                    ).toLocaleString("nl-NL")}`
                  : ""}
              </Typography>
            </Box>
              );
            })()
          ) : null}
        </Box>
        {quarterlyDistribution ? (
          <Box
            sx={{
              mt: 1,
              mb: 0.5,
              px: 1,
              py: 0.75,
              borderRadius: 1,
              border: 1,
              borderColor: "warning.light",
              bgcolor: "warning.lighter",
            }}
          >
            <Typography variant="body2" sx={{ color: "warning.main", fontWeight: 600 }}>
              Year claim - total is evenly distributed across quarters
              {quarterlyDistribution?.yearTotal ? ` (Yearly volume: ${formatNumber(quarterlyDistribution.yearTotal, 2)})` : ""}.
            </Typography>
          </Box>
        ) : null}
        <Typography variant="caption" color="text.secondary" component="div">
          <Box component="span" sx={{ display: "block" }}>
            Tier boundaries = dashed lines
          </Box>
          {hasPricingAgreement && chart.baselineBand ? (
            <Box component="span" sx={{ display: "block" }}>
              Baseline tier band = blue highlight
            </Box>
          ) : null}
          <Box component="span" sx={{ display: "block" }}>
            Last bar = most recent period
          </Box>
          {chart.axisScaleMode === "sqrt" ? (
            <Box component="span" sx={{ display: "block" }}>
              Y-axis is scaled for readability (sqrt)
            </Box>
          ) : null}
        </Typography>
      </Box>

      <Dialog open={isLargeOpen} onClose={() => setIsLargeOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, pr: 1 }}>
          <Typography variant="h6" noWrap>
            {item.countryCode} {productLabel} — Chart (large)
          </Typography>
          <IconButton onClick={() => setIsLargeOpen(false)} size="small" aria-label="Close dialog">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1 }}>
              <Typography variant="subtitle2">{chartSubtitle}</Typography>
              {availableYears.length > 0 ? (
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {availableYears.map((year) => (
                    <Chip
                      key={`large-${year}`}
                      label={String(year)}
                      size="small"
                      variant={year === selectedYear ? "filled" : "outlined"}
                      color={year === selectedYear ? "primary" : "default"}
                      onClick={() => setSelectedYear(year)}
                      aria-label={`Select year ${year}`}
                    />
                  ))}
                </Box>
              ) : null}
            </Box>
            <Box ref={chartWrapLargeRef} sx={{ position: "relative", overflow: "visible" }}>
              <Box sx={{ overflow: "hidden" }}>
                <svg
                  width="100%"
                  height={chartLarge.h}
                  viewBox={`0 0 ${chartLarge.w} ${chartLarge.h}`}
                  preserveAspectRatio="xMidYMid meet"
                  shapeRendering="crispEdges"
                  style={{ display: "block" }}
                >
                  <rect x="0" y="0" width={chartLarge.w} height={chartLarge.h} fill="transparent" />

                  <text
                    x={chartLarge.padding - 8}
                    y={chartLarge.padding + 4}
                    textAnchor="end"
                    fontSize="12"
                    fill={axisTextColor}
                  >
                    {chartLarge.topLabel}
                  </text>
                  <text
                    x={chartLarge.padding - 8}
                    y={chartLarge.h - chartLarge.padding + 4}
                    textAnchor="end"
                    fontSize="12"
                    fill={axisTextColor}
                  >
                    0
                  </text>

                  {chartLarge.yLineLabels.map(({ v, y }) => (
                    <text
                      key={`yline-large-${v}`}
                      x={chartLarge.padding - 8}
                      y={y + 4}
                      textAnchor="end"
                      fontSize="12"
                      fill={axisTextMutedColor}
                    >
                      {Number(v).toLocaleString("nl-NL")}
                    </text>
                  ))}

                  {chartLarge.baselineBand ? (
                    <rect
                      x={chartLarge.padding}
                      y={Math.min(chartLarge.baselineBand.yTop, chartLarge.baselineBand.yBottom)}
                      width={chartLarge.w - chartLarge.padding * 2}
                      height={Math.abs(chartLarge.baselineBand.yBottom - chartLarge.baselineBand.yTop)}
                      fill="rgba(46, 107, 255, 0.12)"
                    />
                  ) : null}

                  {chartLarge.baselineBand ? (
                    <g>
                      <line
                        x1={chartLarge.padding}
                        y1={chartLarge.baselineBand.yTop}
                        x2={chartLarge.w - chartLarge.padding}
                        y2={chartLarge.baselineBand.yTop}
                        stroke={baselineLineColor}
                        strokeWidth="2"
                      />
                      <line
                        x1={chartLarge.padding}
                        y1={chartLarge.baselineBand.yBottom}
                        x2={chartLarge.w - chartLarge.padding}
                        y2={chartLarge.baselineBand.yBottom}
                        stroke={baselineLineColor}
                        strokeWidth="2"
                      />
                    </g>
                  ) : null}

                  {chartLarge.nextTierBand ? (
                    <g>
                      <line
                        x1={chartLarge.padding}
                        y1={chartLarge.nextTierBand.yTop}
                        x2={chartLarge.w - chartLarge.padding}
                        y2={chartLarge.nextTierBand.yTop}
                        stroke={axisLineColor}
                        strokeWidth="2"
                        strokeDasharray="6 6"
                      />
                      <line
                        x1={chartLarge.padding}
                        y1={chartLarge.nextTierBand.yBottom}
                        x2={chartLarge.w - chartLarge.padding}
                        y2={chartLarge.nextTierBand.yBottom}
                        stroke={axisLineColor}
                        strokeWidth="2"
                        strokeDasharray="6 6"
                      />
                    </g>
                  ) : null}

                  {chartLarge.yLines.map((v) => {
                    const y = chartLarge.toY(v);
                    return (
                      <line
                        key={`line-large-${v}`}
                        x1={chartLarge.padding}
                        y1={y}
                        x2={chartLarge.w - chartLarge.padding}
                        y2={y}
                        stroke={axisLineColor}
                        strokeDasharray="6 6"
                      />
                    );
                  })}

                  <rect
                    x={chartLarge.padding}
                    y={chartLarge.padding}
                    width={chartLarge.w - chartLarge.padding * 2}
                    height={chartLarge.h - chartLarge.padding * 2}
                    fill="none"
                    stroke={borderLineColor}
                  />

                  {chartLarge.bars.map((b, idx) => (
                    <rect
                      key={`bar-large-${b.label}-${idx}`}
                      x={b.x}
                      y={b.y}
                      width={b.width}
                      height={b.height}
                      rx="0"
                      fill={
                        b.isInBaseline
                          ? idx === chartLarge.bars.length - 1
                            ? "rgba(46, 224, 122, 0.90)"
                            : "rgba(46, 224, 122, 0.65)"
                          : idx === chartLarge.bars.length - 1
                          ? "rgba(46, 107, 255, 0.85)"
                          : "rgba(46, 107, 255, 0.55)"
                      }
                      onMouseEnter={() => setHoveredBarLarge(b)}
                      onMouseLeave={() => setHoveredBarLarge(null)}
                    />
                  ))}

                  {chartLarge.bars.map((b, idx) => (
                    <text
                      key={`lbl-large-${b.label}-${idx}`}
                      x={b.x + b.width / 2}
                      y={chartLarge.h - 12}
                      textAnchor="middle"
                      fontSize="12"
                      fill={axisTextColor}
                    >
                      {b.label}
                    </text>
                  ))}
                </svg>
              </Box>

              {hoveredBarLarge ? (
                (() => {
                  const tooltipWidth = 260;
                  const half = tooltipWidth / 2;
                  const minX = chartLarge.padding * chartScaleLarge + half + 6;
                  const maxX = (chartLarge.w - chartLarge.padding) * chartScaleLarge - half - 6;
                  const centerX = (hoveredBarLarge.x + hoveredBarLarge.width / 2) * chartScaleLarge;
                  const left = clamp(centerX, minX, maxX);
                  return (
                    <Box
                      sx={{
                        position: "absolute",
                        left,
                        top: Math.max(8, hoveredBarLarge.y * chartScaleLarge - 8),
                        transform: "translate(-50%, -100%)",
                        bgcolor: "background.paper",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 0.75,
                        boxShadow: 3,
                        pointerEvents: "none",
                        minWidth: tooltipWidth,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {hoveredBarLarge.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {Number(hoveredBarLarge.value ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 2 })} (VAT volume)
                      </Typography>
                      {Number(hoveredBarLarge.periodMonths ?? 1) > 1 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          Avg/month: {Number(hoveredBarLarge.avgMonthly ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 2 })}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        Scale: {hoveredBarLarge.barScale}
                        {hoveredTierLine
                          ? ` • Range: ${Number(hoveredTierLine.from * Number(hoveredBarLarge.periodMonths ?? 1)).toLocaleString(
                              "nl-NL"
                            )} – ${Number(hoveredTierLine.to * Number(hoveredBarLarge.periodMonths ?? 1)).toLocaleString("nl-NL")}`
                          : ""}
                      </Typography>
                    </Box>
                  );
                })()
              ) : null}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {yearCatchups.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Catch-up yearly claims
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="right">Regular volume</TableCell>
                  <TableCell align="right">Catch-up volume</TableCell>
                  <TableCell align="right">Year total volume</TableCell>
                  <TableCell align="right">Baseline fee/year</TableCell>
                  <TableCell align="right">Actual fee/year</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {yearCatchups.map((row) => {
                  const vatVolumeYearTotal = Number(row?.vatVolumeYearTotal ?? 0);
                  const actualFeeYear = Number(row?.actualFeeYear ?? 0);
                  const baselineFeeYear = Number(row?.baselineFeeYear ?? 0);
                  const actualVolumeMonthly = vatVolumeYearTotal / 12;
                  const actualMonthFee = actualFeeYear / 12;
                  const deltaMonthFee = (actualFeeYear - baselineFeeYear) / 12;

                  return (
                    <React.Fragment key={`${row?.year ?? "year"}-${row?.vatVolume ?? 0}`}>
                      <TableRow>
                        <TableCell align="right">{formatNumber(vatVolumeYearTotal - Number(row?.vatVolume ?? 0), 2)}</TableCell>
                        <TableCell align="right">{formatNumber(row?.vatVolume, 2)}</TableCell>
                        <TableCell align="right">{formatNumber(vatVolumeYearTotal, 2)}</TableCell>
                        <TableCell align="right">{formatCurrencyEUR(baselineFeeYear)}</TableCell>
                        <TableCell align="right">{formatCurrencyEUR(actualFeeYear)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={5} sx={{ bgcolor: "background.default" }}>
                          <Typography variant="caption" color="text.secondary" component="div">
                            Actual volume (month) = vatVolumeYearTotal / 12 = {formatNumber(actualVolumeMonthly, 2)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            Actual month fee = actualFeeYear / 12 = {formatCurrencyEUR(actualMonthFee)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            Delta month fee = (actualFeeYear - baselineFeeYear) / 12 = {formatCurrencyEUR(deltaMonthFee)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}
    </Box>
  );
};

export default FeeMonitorTrendPanel;


